import { Button, Dropdown, Space } from "antd";
import {
  DownloadOutlined,
  MoreOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import useIsMobile from "../../hooks/useIsMobile";
import { isIos } from "./InstallPrompt";

/**
 * Header actions for the PDF preview drawers (invoices, quotes, payslips...).
 *
 * These used to be rendered inside the drawer `title`, where they overlapped the
 * title text and the drawer's own close control as soon as the viewport got
 * narrow. Keeping them in one place means every preview drawer gets the same
 * layout: real buttons on desktop, a three-dot action menu below `lg`.
 */
const PreviewDrawerActions = ({
  onPrint,
  onDownload,
  onClose,
  printDisabled = false,
  downloadDisabled = false,
  downloadLoading = false,
  downloadLabel = "Download PDF",
}) => {
  const isMobile = useIsMobile(991);

  // iOS has no usable print path here: in a home-screen app `window.open` is
  // either blocked or bounces the user out to Safari, and the follow-up
  // `printWindow.print()` on a cross-document PDF does not fire. Offering a
  // button that silently does nothing is worse than not offering it — Download
  // hands the PDF to the iOS share sheet, which can print.
  const canPrint = onPrint && !isIos();

  const items = [];
  if (canPrint) {
    items.push({
      key: "print",
      icon: <PrinterOutlined />,
      label: "Print",
      disabled: printDisabled,
    });
  }
  if (onDownload) {
    items.push({
      key: "download",
      icon: <DownloadOutlined />,
      label: downloadLabel,
      disabled: downloadDisabled || downloadLoading,
    });
  }

  const handleMenuClick = ({ key }) => {
    if (key === "print") onPrint();
    if (key === "download") onDownload();
  };

  if (isMobile) {
    return (
      <Space size={4}>
        {items.length > 0 && (
          <Dropdown
            menu={{ items, onClick: handleMenuClick }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button icon={<MoreOutlined />} aria-label="More actions" />
          </Dropdown>
        )}
        {onClose && <Button onClick={onClose}>Close</Button>}
      </Space>
    );
  }

  return (
    <Space size={8}>
      {canPrint && (
        <Button
          icon={<PrinterOutlined />}
          onClick={onPrint}
          disabled={printDisabled}
        >
          Print
        </Button>
      )}
      {onDownload && (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={onDownload}
          disabled={downloadDisabled}
          loading={downloadLoading}
        >
          {downloadLabel}
        </Button>
      )}
      {onClose && <Button onClick={onClose}>Close</Button>}
    </Space>
  );
};

export default PreviewDrawerActions;
